import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';

import { useProcurement } from '../procurement/context/ProcurementContext';
import SupplierContractTermCreateModal from '../supplier_contracts/terms/SupplierContractTermCreateModal';

import RevenueContractsIndexTableRow from './RevenueContractsIndexTableRow';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient } from 'Helpers/network';

import BulkDeleteButtons from 'CommonComponents/BulkDeleteButtons';
import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import SelectField from 'CommonComponents/SelectField';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';

import useBulkDelete from 'Hooks/useBulkDelete';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

import FileExportIcon from 'Assets/icons/file-export.svg';

const UPSERT_TERMS_MODAL_ID = 'add-supplier-contract-terms-modal';
const DELETE_TERMS_MODAL_ID = 'delete-supplier-contract-term-modal';
const DELETE_TERM_ATTACHMENT_MODAL_ID = 'delete-supplier-contract-term-attachment-modal';
const DELETE_REVENUE_CONTRACT_MODAL_ID = 'delete-revenue-contract-modal';
const CANCEL_REVENUE_CONTRACT_MODAL_ID = 'cancel-revenue-contract-modal';

export default function RevenueContractsTableIndex(props) {
    const [revenues, setRevenues] = useState([]);
    const [supplierContractTerms, setSupplierContractTerms] = useState(null);
    const [supplierContractTermAttachment, setSupplierContractTermAttachment] = useState(null);
    const [isLoading, setIsLoading] = useState({});
    const [currentContract, setCurrentContract] = useState(null);
    const [contractType, setContractType] = useState(null);
    const [committedId, setCommittedId] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);
    const [cancelContract, setCancelContract] = useState({});
    const [deleteContract, setDeleteContract] = useState({});

    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedOwner, setSelectedOwner] = useState(null);

    const [supplierContractTermAttachmentDeleteModalElement, setSupplierContractTermAttachmentDeleteModalElement] = useState(null);
    const [supplierContractTermDeleteModalElement, setSupplierContractTermDeleteModalElement] = useState(null);
    const [revenueContractDeleteModalElement, setRevenueContractDeleteModalElement] = useState(null);
    const [revenueContractCancelModalElement, setRevenueContractCancelModalElement] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');

    const supplierContractTermAttachmentDeleteModalRef = useRef();
    const supplierContractTermDeleteModalRef = useRef();
    const revenueContractDeleteModalRef = useRef();
    const revenueContractCancelModalRef = useRef();

    const supplierContractTermCreateModal = useModal();

    const { users } = useUsers();

    const toast = useToast();

    const confirm = useConfirmationModal();

    const { routes, settings } = useProcurement();

    const { toggleSelecting, selecting, selectedItems, toggleItem } = useBulkDelete();

    const bulkDeleteContractsRequest = useQuery({
        enabled: false,
        queryFn: ({ ids, all }) => apiClient.delete(routes.revenueContractBulkDeleteUrl, { data: { ids, all, action: props.forCancel ? 'delete' : 'cancel' } }),
        successFn: () => {
            toast.success('Items removed successfully');
            props.triggerRerender();
            toggleSelecting();
        },
    });

    const pagination = usePagination({ perPage: 25 });

    useEffect(() => {
        fetchTicketingRevenueCommitment();

        supplierContractTermAttachmentDeleteModalRef &&
            setSupplierContractTermAttachmentDeleteModalElement(new Modal(supplierContractTermAttachmentDeleteModalRef.current, modalOptions(false)));
        supplierContractTermDeleteModalRef &&
            setSupplierContractTermDeleteModalElement(new Modal(supplierContractTermDeleteModalRef.current, modalOptions(false)));
        revenueContractCancelModalRef && setRevenueContractCancelModalElement(new Modal(revenueContractCancelModalRef.current, modalOptions(false)));
        revenueContractDeleteModalRef && setRevenueContractDeleteModalElement(new Modal(revenueContractDeleteModalRef.current, modalOptions(false)));
    }, [pagination.page, pagination.perPage, searchQuery]);

    useEffect(() => {
        if (Object.keys(cancelContract).length > 0) {
            revenueContractCancelModalElement && revenueContractCancelModalElement.show();
        }
    }, [cancelContract]);

    useEffect(() => {
        if (Object.keys(deleteContract).length > 0) {
            revenueContractDeleteModalElement && revenueContractDeleteModalElement.show();
        }
    }, [deleteContract]);

    const fetchSupplierContractTerms = (contractId) => {
        const url = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contractId);
        loading('supplier_contract_terms');

        axios
            .get(url)
            .then(({ data }) => {
                setSupplierContractTerms(data);
            })
            .catch((error) => console.error(error))
            .finally(() => loading('supplier_contract_terms', false));
    };

    const deleteAttachment = (attachment) => {
        setSupplierContractTermAttachment(attachment);
        showModalElement(supplierContractTermAttachmentDeleteModalElement);
    };

    const downloadAttachment = (e, attachment) => {
        e.preventDefault();
        const url = props.downloadSupplierContractsTermAttachmentUrl
            .replace('CONTRACT_ID', currentContract?.id)
            .replace('TERM_ID', attachment.term_id)
            .replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    const showModalElement = (modalElement) => {
        modalElement && modalElement.show();
    };

    const onAddTerms = (contract) => {
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        supplierContractTermCreateModal.show();
    };

    const onViewTerms = (contract) => {
        window.location.href = props.supplierContractTermsIndexUrl.replace('CONTRACT_ID', contract.id);
    };

    const onEditTerms = (contract) => {
        // Drop the previous contract's terms first: a failed fetch leaves them in place otherwise,
        // and the modal would open on this contract showing the last one's values.
        setSupplierContractTerms(null);
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        supplierContractTermCreateModal.show();
    };

    const onDeleteTerms = (contract) => {
        setCurrentContract(contract);
        fetchSupplierContractTerms(contract.id);
        showModalElement(supplierContractTermDeleteModalElement);
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const onBulkDeleteContracts = () => {
        confirm({
            title: $t('Delete Contracts'),
            message: selecting ? $t('Are you sure you want to delete the selected contracts?') : $t('Are you sure you want to delete all contracts?'),
            warning: $t('Deleting a contract will also delete commitments linked to this contract'),
            onConfirm: () => triggerBulkDeleteContracts(),
        });
    };

    const triggerBulkDeleteContracts = () => {
        let ids = selectedItems;

        if (selecting && !ids.length) {
            return;
        }

        bulkDeleteContractsRequest.refetch({ ids, all: !selecting });
    };

    const triggerDeleteAttachment = () => {
        setActionInProgress(true);

        const url = props.deleteSupplierContractsTermAttachmentUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id)
            .replace('ATTACHMENT_ID', supplierContractTermAttachment.id);

        axios
            .delete(url)
            .then(() => {
                supplierContractTermAttachmentDeleteModalElement && supplierContractTermAttachmentDeleteModalElement.hide();
                fetchSupplierContractTerms(currentContract?.id);
            })
            .catch(() => setActionFailed(true))
            .finally(() => setActionCompleted(true));
    };

    const triggerDeleteTerm = () => {
        setActionInProgress(true);

        const url = props.supplierContractTermDeleteUrl
            .replace('SUPPLIER_CONTRACT_ID', supplierContractTerms?.supplier_contract_id)
            .replace('TERM_ID', supplierContractTerms?.id);

        axios
            .delete(url)
            .then(() => {
                supplierContractTermDeleteModalElement && supplierContractTermDeleteModalElement.hide();
                fetchTicketingRevenueCommitment();
            })
            .catch(() => setActionFailed(true))
            .finally(() => setActionCompleted(true));
    };

    const loading = (key, value = true) => {
        setIsLoading((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const fetchTicketingRevenueCommitment = () => {
        loading('fetchTicketingRevenueCommitment');

        axios
            .get(props.fetchUrl, {
                params: {
                    cancelled_contracts: props.forCancel,
                    closed_contracts: props.forClosed,
                    page: pagination.page,
                    per_page: pagination.perPage,
                    search: searchQuery,
                    ...(selectedDepartment ? { department_id: selectedDepartment.value } : {}),
                    ...(selectedOwner ? { owner_id: selectedOwner.value } : {}),
                },
            })
            .then(({ data }) => {
                setRevenues(data.data);
                pagination.setTotal(data.total);
                pagination.setLastPage(data.last_page);
                pagination.setFrom(data.from);
                pagination.setTo(data.to);
            })
            .catch((e) => {
                console.error(e);
                loading('fetchTicketingRevenueCommitment', false);
            })
            .finally(() => {
                loading('fetchTicketingRevenueCommitment', false);
            });
    };

    const onCancelContract = (contract, contract_type, committed_id) => {
        setCancelContract(contract);
        setContractType(contract_type);
        setCommittedId(committed_id);
    };

    const onDeleteContract = (contract, contract_type, committed_id) => {
        setDeleteContract(contract);
        setContractType(contract_type);
        setCommittedId(committed_id);
    };

    const sendDeleteContractRequest = (deleteContractUrl) => {
        setActionInProgress(true);

        axios
            .delete(deleteContractUrl)
            .then(() => {
                revenueContractDeleteModalElement && revenueContractDeleteModalElement.hide();
                revenueContractCancelModalElement && revenueContractCancelModalElement.hide();
                props.triggerRerender();
            })
            .catch(() => setActionFailed(true))
            .finally(() => setActionCompleted(true));
    };

    const triggerCancelContract = () => {
        const url = props.revenueContractDeleteUrl
            .replace('CONTRACT_ID', cancelContract?.id)
            .replace('CONTRACT_TYPE', contractType)
            .replace('COMMITTED_ID', committedId);

        sendDeleteContractRequest(url);
    };

    const triggerDeleteContract = () => {
        const url = props.revenueContractDeleteUrl
            .replace('CONTRACT_ID', deleteContract?.id)
            .replace('CONTRACT_TYPE', contractType)
            .replace('COMMITTED_ID', committedId);

        sendDeleteContractRequest(url);
    };

    const successHandler = () => {
        setDeleteContract({});
        setCancelContract({});
        setContractType(null);
        setCommittedId(null);
        fetchTicketingRevenueCommitment();
        revenueContractCancelModalElement && revenueContractCancelModalElement.hide();
        revenueContractDeleteModalElement && revenueContractDeleteModalElement.hide();
    };

    const closeModal = () => {
        setDeleteContract({});
        setCancelContract({});
        setContractType(null);
        setCommittedId(null);
        revenueContractCancelModalElement && revenueContractCancelModalElement.hide();
        revenueContractDeleteModalElement && revenueContractDeleteModalElement.hide();
    };

    const getTableTitle = () => {
        let title = $t('Revenue Contracts');
        if (props.forCancel) {
            title = $t('Cancelled Revenue Contracts');
        } else if (props.forClosed) {
            title = $t('Closed Revenue Contracts');
        }

        return title;
    };

    const handleFilterApply = () => {
        if (pagination.page === 1) {
            // already on page 1 → just fetch directly
            fetchTicketingRevenueCommitment();
        } else {
            // jump to page 1 → this will trigger onChange effect
            pagination.setPage(1);
        }
    };

    return (
        <>
            <div className="table__card">
                <div className="tw-flex tw-justify-between tw-mb-4">
                    <div>
                        <h4 className="table__header">{getTableTitle()}</h4>
                        <div className="tw-flex tw-items-center tw-space-x-3">
                            <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                            <TablePagination
                                lastPage={pagination.lastPage}
                                pageChange={(data) => {
                                    pagination.setPage(+data.selected + 1);
                                }}
                                forcePage={pagination.page - 1}
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-space-x-2">
                        <div>
                            <PerPageSelect
                                value={pagination.perPage}
                                onChange={(e) => {
                                    pagination.setPerPage(parseInt(e.target.value));
                                }}
                            />
                        </div>
                        <div>
                            <TableSearchInput searchValue={searchQuery} handleEnterPress={(e, value) => setSearchQuery(value)} />
                        </div>
                        <BulkDeleteButtons
                            selecting={selecting}
                            selectedCount={selectedItems.length}
                            itemsCount={revenues?.length || 0}
                            loading={bulkDeleteContractsRequest.loading}
                            onDelete={() => onBulkDeleteContracts()}
                            toggleSelecting={toggleSelecting}
                        />
                        <div>
                            <TableFilterModal headerText={$t('Filters')} onApply={handleFilterApply} modalBodyClassNames="tw-w-128">
                                <div className="tw-flex tw-flex-col tw-gap-3">
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label htmlFor="department" className="text-input__label">
                                            {$t('Select a department')}
                                        </label>
                                        <SelectField
                                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                            options={props.revDepartmentsOptions}
                                            value={selectedDepartment}
                                            onChange={(department) => setSelectedDepartment(department)}
                                            backgroundColor="#e4e4e480"
                                            height={32}
                                            clearable
                                            menuPortalTarget={null}
                                        />
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                        <label htmlFor="owner" className="text-input__label">
                                            {$t('Select an owner')}
                                        </label>
                                        <SelectField
                                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold placeholder:tw-text-charcoal-75 placeholder:tw-text-sm placeholder:tw-font-normal"
                                            options={props.ownersOptions}
                                            value={selectedOwner}
                                            onChange={(owner) => setSelectedOwner(owner)}
                                            backgroundColor="#e4e4e480"
                                            height={32}
                                            clearable
                                            menuPortalTarget={null}
                                        />
                                    </div>
                                </div>
                            </TableFilterModal>
                        </div>
                    </div>
                </div>

                <div className="inbud-table_wrapper">
                    <table className="inbud-table tw-table-fixed">
                        <thead className="tw-capitalize">
                            <tr>
                                <th rowSpan={props.forDeletedContracts ? 1 : 2} className={classNames('tw-w-8', { 'tw-hidden': !selecting })}>
                                    {!!selectedItems.length && (
                                        <div className="tw-rounded-full tw-h-4 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-2xs tw-text-white tw-bg-red-75">
                                            {selectedItems.length}
                                        </div>
                                    )}
                                </th>
                                <th className="tw-w-28">{$t('Contract')}</th>
                                <th className="tw-w-14">{$t('Signed')}</th>
                                <th className="tw-w-40">{$t('Supplier Name')}</th>
                                <th className="tw-w-80">{$t('Department')}</th>
                                <th className="tw-w-32">{$t('Contracted')}</th>
                                <th className="tw-w-24">{$t('Actuals')}</th>
                                <th className="tw-w-24">{$t('Variance')}</th>
                                <th className="tw-border-r-2 tw-w-40">{$t('Owner')}</th>
                                <th className="!tw-pb-1.5 tw-w-40 tw-border-r-2 !tw-text-center">{$t('Payment milestones')}</th>
                                <th className="!tw-pb-1.5 tw-w-40 tw-border-r-2 !tw-text-center">{$t('Other milestones')}</th>
                                <th className="tw-w-128">{$t('Terms Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading.fetchTicketingRevenueCommitment && <TableRowSkeleton rows={10} columns={11} />}

                            {!isLoading.fetchTicketingRevenueCommitment &&
                                !!revenues?.length &&
                                revenues.map(
                                    (revenue) =>
                                        revenue.contract && (
                                            <RevenueContractsIndexTableRow
                                                key={_.snakeCase(`${revenue.contract.id}_${revenue.depcc}_${revenue.committed_id}`)}
                                                forCancel={props.forCancel}
                                                revenue={revenue}
                                                cancelRevenueCommitmentUrl={props.cancelRevenueCommitmentUrl}
                                                selecting={selecting}
                                                selectedIds={selectedItems}
                                                isSupplierPortalEnabled={settings.isSupplierPortalEnabled}
                                                onSelect={(contractId) => toggleItem(contractId)}
                                                updateSignedContractUrl={props.updateSignedContractUrl}
                                                onAddTerms={onAddTerms}
                                                onViewTerms={onViewTerms}
                                                onEditTerms={onEditTerms}
                                                onDeleteContract={onCancelContract}
                                                onDeletingAlreadyCancelledContract={onDeleteContract}
                                                fetchTicketingRevenueCommitment={fetchTicketingRevenueCommitment}
                                            />
                                        ),
                                )}

                            {!isLoading.fetchTicketingRevenueCommitment && !revenues?.length && (
                                <tr>
                                    <td colSpan={11}>{$t('No revenue contracts')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-x-2 tw-mt-4">
                    <Button variant="primary" as="a" href={props.exportToFile} data-action="navigate">
                        <FileExportIcon className="tw-w-3 tw-h-3" />
                        <div>{$t('Export to file')}</div>
                    </Button>
                </div>
            </div>
            <SupplierContractTermCreateModal
                modal={supplierContractTermCreateModal}
                isLoading={isLoading.supplier_contract_terms}
                storeUrl={props.supplierContractTermStoreUrl}
                updateUrl={props.supplierContractTermUpdateUrl}
                contract={currentContract}
                terms={supplierContractTerms}
                users={users}
                onCreated={() => fetchTicketingRevenueCommitment()}
                onDeleteAttachment={deleteAttachment}
                onDownloadAttachment={downloadAttachment}
                eventStartDate={props.eventStartDate}
                eventEndDate={props.eventEndDate}
            />

            <ConfirmationModal
                triggerAction={triggerDeleteAttachment}
                cleanStates={cleanConfirmationModalStates}
                allowAction={supplierContractTermAttachment != null}
                modalId={DELETE_TERM_ATTACHMENT_MODAL_ID}
                modalRef={supplierContractTermAttachmentDeleteModalRef}
                modalElement={supplierContractTermAttachmentDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: supplierContractTermAttachment?.file_name })}
                actionCompletedMsg=""
                actionFailedMsg={$t('{{name}} could not be deleted', { name: supplierContractTermAttachment?.file_name })}
                title={$t('Delete attachment')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />

            <ConfirmationModal
                triggerAction={triggerDeleteTerm}
                cleanStates={cleanConfirmationModalStates}
                allowAction={currentContract != null}
                modalId={DELETE_TERMS_MODAL_ID}
                modalRef={supplierContractTermDeleteModalRef}
                modalElement={supplierContractTermDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete terms of contract {{ref}}?', { ref: currentContract?.contract_reference })}
                actionCompletedMsg=""
                actionFailedMsg={$t('contract {{ref}} terms could not be deleted', { ref: currentContract?.contract_reference })}
                title={$t('Delete attachment')}
                cancelButtonText="Cancel"
                primaryButtonText="Delete"
            />

            <ConfirmationModal
                closeHandler={closeModal}
                successHandler={successHandler}
                triggerAction={triggerCancelContract}
                cleanStates={cleanConfirmationModalStates}
                allowAction={Object.keys(cancelContract).length > 0}
                modalId={CANCEL_REVENUE_CONTRACT_MODAL_ID}
                modalRef={revenueContractCancelModalRef}
                modalElement={revenueContractCancelModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete contract {{ref}}?', { ref: cancelContract?.contract_reference })}
                warningMsg={$t(
                    'Deleting a contract will delete the commitment and budget linked to this contract. The quantities committed will be added back to the current uncommitted quantity and inherit the current uncommitted price. You may need to go back into the line item and adjust the budget accordingly.',
                )}
                actionCompletedMsg={$t('Contract {{ref}} deleted successfully', { ref: cancelContract?.contract_reference })}
                actionFailedMsg=""
                title={$t('Delete Contract')}
                cancelButtonFirst={true}
                submitButtonVariant="primary"
                cancelButtonVariant="danger"
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Confirm')}
            />

            <ConfirmationModal
                closeHandler={closeModal}
                successHandler={successHandler}
                triggerAction={triggerDeleteContract}
                cleanStates={cleanConfirmationModalStates}
                allowAction={Object.keys(deleteContract).length > 0}
                modalId={DELETE_REVENUE_CONTRACT_MODAL_ID}
                modalRef={revenueContractDeleteModalRef}
                modalElement={revenueContractDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to permanently delete contract {{ref}}?', { ref: deleteContract?.contract_reference })}
                actionCompletedMsg={$t('Contract {{ref}} deleted successfully', { ref: deleteContract?.contract_reference })}
                actionFailedMsg=""
                title={$t('Delete Contract')}
                cancelButtonFirst={true}
                submitButtonVariant="primary"
                cancelButtonVariant="danger"
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Confirm')}
            />
        </>
    );
}
